import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { CharacterLimit } from '~/eds/src/components/CharacterLimit/CharacterLimit';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { IconType } from '~/eds/src/types/IconType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';

const messages = defineMessages({
  newGroup: {
    id: 'GroupFormModal.newGroup',
    defaultMessage: 'New Group',
    description: 'Modal title for creating a new group',
  },
  groupName: {
    id: 'GroupFormModal.groupNameLabel',
    defaultMessage: 'Group Name',
    description: 'Label for the group name input field',
  },
  groupNamePlaceholder: {
    id: 'GroupFormModal.groupNamePlaceholder',
    defaultMessage: 'Enter group name',
    description: 'Placeholder text for the group name input field',
  },
  note: {
    id: 'GroupFormModal.noteLabel',
    defaultMessage: 'Note',
    description: 'Label for the note textarea field',
  },
  notePlaceholder: {
    id: 'GroupFormModal.notePlaceholder',
    defaultMessage: 'Enter note',
    description: 'Placeholder text for the note textarea field',
  },
});

export interface GroupFormData {
  name: string;
  note: string;
}

interface GroupFormModalProps {
  isVisible: boolean;
  onHide: () => void;
  onSubmit: (data: GroupFormData) => Promise<void>;
  submitButtonText: string;
  title?: string;
  submitButtonIcon?: IconType;
  initialName?: string;
  initialNote?: string;
  isLoading?: boolean;
}

export const GroupFormModal = ({
  isVisible,
  onHide,
  onSubmit,
  title,
  submitButtonText,
  submitButtonIcon,
  initialName = '',
  initialNote = '',
  isLoading = false,
}: GroupFormModalProps) => {
  const { formatMessage } = useIntl();
  const enableModalConsolidation = useFlag(FlagType.ModalConsolidation);
  const [groupName, setGroupName] = useState(initialName);
  const [groupNote, setGroupNote] = useState(initialNote);

  useEffect(() => {
    if (isVisible) {
      setGroupName(initialName);
      setGroupNote(initialNote);
    }
  }, [isVisible, initialName, initialNote]);

  const resetForm = () => {
    setGroupName('');
    setGroupNote('');
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleSubmit = async () => {
    await onSubmit({
      name: groupName,
      note: groupNote,
    });
    handleClose();
  };

  const modalTitle = title ?? formatMessage(messages.newGroup);

  const children = (
    <Layout preset="form-fields">
      <FormField<string, false>
        name="groupName"
        label={formatMessage(messages.groupName)}
        placeholder={formatMessage(messages.groupNamePlaceholder)}
        value={groupName}
        input={TextInput}
        required
        onChange={(value) => setGroupName(value ?? '')}
      />
      <Layout direction="column">
        <FormField<string, false>
          name="groupNote"
          label={formatMessage(messages.note)}
          placeholder={formatMessage(messages.notePlaceholder)}
          value={groupNote}
          input={TextArea}
          inputProps={{
            enableAutoResize: true,
            maxLength: 300,
          }}
          onChange={(value) => setGroupNote(value ?? '')}
        />
        {enableModalConsolidation && (
          <CharacterLimit limit={300} text={groupNote} />
        )}
      </Layout>
    </Layout>
  );

  if (enableModalConsolidation && isVisible) {
    return (
      <Panel
        enableBackdrop
        mode="floating"
        width="560px"
        title={modalTitle}
        hidden={{ onHide: handleClose }}
        footer={{
          actions: [
            {
              text: formatMessage({
                id: 'GroupFormModal.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              }),
              onClick: handleClose,
              mode: 'chip',
            },
            {
              mode: 'chip',
              text: submitButtonText,
              icon: submitButtonIcon,
              level: 'primary',
              disabled: !groupName.trim(),
              isLoading,
              onClick: handleSubmit,
            },
          ],
        }}
        children={children}
      />
    );
  }

  return (
    <Modal
      isVisible={isVisible}
      title={modalTitle}
      primaryAction={{
        text: submitButtonText,
        icon: submitButtonIcon,
        disabled: !groupName.trim(),
        isLoading,
        onClick: handleSubmit,
      }}
      onHide={handleClose}
    >
      {children}
    </Modal>
  );
};
